import React, {
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CalendarLanguage,
  CalendarView,
  ptbrCalendarLanguage,
} from '../../utils/CalendarUtils';
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils';
import './Calendar.scss';
import CalendarHeader from './components/calendarHeader/CalendarHeader';
import CalendarDays from './components/calendarDays/CalendarDays';
import { CalendarEventProps } from '../calendarEvent/CalendarEvent';
const rootClassName = 'calendar';

export interface CalendarProps {
  visao?: CalendarView;
  language?: CalendarLanguage;
  eventos?: Map<string, CalendarEventProps[]>;
  loading?: boolean;
  referencia?: Date;
  disabledNextMonth?: boolean;
  disabledPrevMonth?: boolean;
  disabledPrevAddButton?: boolean;
  highlightWeekends?: boolean;
  showAddButton?: boolean;
  onAddButtonClick?: (
    day: Date,
    evt: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
  showPlusIcon?: boolean;
  onPlusButtonClick?: (
    day: Date,
    evt: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
  onDateClick?: (
    day: Date,
    evt: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
}

function Calendar({
  language = ptbrCalendarLanguage,
  visao = CalendarView.Mensal,
  referencia = new Date(),
  ...props
}: CalendarProps) {
  const id = useMemo(getUniqueKey, []);

  const [pocket, setPocket] = useState(false);
  const pocketRef = useRef(pocket);
  pocketRef.current = pocket;

  const classNames = useMemo(() => {
    return getMergedClassNames([rootClassName, props.loading ? 'loading' : '']);
  }, [props.loading]);

  const cbResize = useCallback((evt: any) => {
    if (pocketRef.current) {
      if (evt[0].contentBoxSize[0].inlineSize > 805) {
        setPocket(false);
      }
    } else {
      if (evt[0].contentBoxSize[0].inlineSize < 794) {
        setPocket(true);
      }
    }
  }, []);

  useEffect(() => {
    const calendar = document.querySelector(`#${id}`) as HTMLElement;
    const observer = new window.ResizeObserver(cbResize);
    if (calendar) {
      observer.observe(calendar);
    }
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pocket]);

  return (
    <div id={id} className={classNames}>
      <CalendarHeader language={language} />
      <CalendarDays
        pocket={pocket}
        visao={visao}
        referencia={referencia}
        disabledNextMonth={props.disabledNextMonth}
        disabledPrevMonth={props.disabledPrevMonth}
        disabledPrevAddButton={props.disabledPrevAddButton}
        language={language}
        showAddButton={props.showAddButton}
        onAddButtonClick={props.onAddButtonClick}
        showPlusIcon={props.showPlusIcon}
        onPlusButtonClick={props.onPlusButtonClick}
        onDateClick={props.onDateClick}
        highlightWeekends={props.highlightWeekends}
        loading={props.loading}
        eventos={props.eventos}
      />
    </div>
  );
}

Calendar.defaultProps = {
  loading: false,
  disabledNextMonth: true,
  disabledPrevMonth: true,
  disabledPrevAddButton: false,
  highlightWeekends: true,
  showAddButton: true,
  showPlusIcon: true,
} as CalendarProps;

export default memo(Calendar);
